"use client";

import { useEffect, useRef, useCallback } from "react";
import { HeroSection } from "./sections/HeroSection";
import { SpecialistSection } from "./sections/SpecialistSection";
import { PrinciplesSection } from "./sections/PrinciplesSection";
import { AntiStressSection } from "./sections/AntiStressSection";
import { EmmaAgasaryanSection } from "./sections/EmmaAgasaryanSection";
import { SelectionProcessSection } from "./sections/SelectionProcessSection";
import { ValuesSection } from "./sections/ValuesSection";
import { FAQSection } from "./sections/FAQSection";
import type { SectionMeta } from "@/entities/section";
import type { TeamMember } from "@/entities/team";
import type { ContactChannel } from "@/entities/contacts";
import type { FaqItem } from "@/entities/faq";
import type { Specialist } from "@/entities/specialist";
import type { Value } from "@/entities/value";
import type { Approach } from "@/entities/approach";
import type { Principle } from "@/entities/principle";
import type { SelectionStep } from "@/entities/selection-step";

interface HelpPageProps {
  section: SectionMeta;
  teamMembers: TeamMember[];
  contactChannels: ContactChannel[];
  faqs: FaqItem[];
  specialists: Specialist[];
  values: Value[];
  approaches: Approach[];
  emmaQuote: string;
  principles: Principle[];
  selectionSteps: SelectionStep[];
  onPageChange?: (index: number) => void;
}

export function HelpPage({
  section,
  teamMembers,
  contactChannels,
  faqs,
  specialists,
  values,
  approaches,
  emmaQuote,
  principles,
  selectionSteps,
  onPageChange,
}: HelpPageProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  const setRef = useCallback((el: HTMLElement | null, i: number) => {
    sectionRefs.current[i] = el;
  }, []);

  // Hash-scroll: when navigating with scroll={false} pill Links OR modal close,
  // manually scroll to the hash target. Polls up to 30 frames (~500ms) waiting
  // for the element to gain layout (handles TriadRing display:none → block transition).
  // Triggers on both initial mount AND subsequent hashchange events (modal CTA → /help#xxx).
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cleanupCurrent: (() => void) | null = null;

    const scrollToHash = () => {
      // Clean up any in-progress scroll loop from a previous trigger
      if (cleanupCurrent) cleanupCurrent();

      const hash = window.location.hash;
      if (!hash) return;
      const id = hash.slice(1);

      let cancelled = false;
      let attempts = 0;
      let rafId = 0;

      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.height > 0 || rect.width > 0) {
            el.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "start" });
            return;
          }
        }
        if (attempts++ < 30) {
          rafId = requestAnimationFrame(tryScroll);
        }
      };

      rafId = requestAnimationFrame(tryScroll);
      cleanupCurrent = () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
      };
    };

    // Initial scroll (on mount)
    scrollToHash();

    // Re-scroll on hash change (e.g., modal "Записаться" → /help#contact-form)
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      if (cleanupCurrent) cleanupCurrent();
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.35) {
            const idx = sectionRefs.current.indexOf(e.target as HTMLElement);
            if (idx !== -1) onPageChange?.(idx);
          }
        }
      },
      { root, threshold: 0.35 },
    );
    sectionRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [onPageChange]);

  return (
    <div ref={scrollRef} className="scroll-smooth flex flex-col gap-fluid-2xl">
      <HeroSection ref={(el) => setRef(el, 0)} />
      <SpecialistSection ref={(el) => setRef(el, 1)} specialists={specialists} />
      <EmmaAgasaryanSection ref={(el) => setRef(el, 2)} quote={emmaQuote} principles={principles} />
      <SelectionProcessSection steps={selectionSteps} />
      <ValuesSection values={values} />
      <PrinciplesSection ref={(el) => setRef(el, 3)} approaches={approaches} specialists={specialists} />
      <AntiStressSection ref={(el) => setRef(el, 4)} />
      <FAQSection
        ref={(el) => setRef(el, 5)}
        faqs={faqs}
        teamMembers={teamMembers}
        contactChannels={contactChannels}
        accentColor={section.color}
      />
    </div>
  );
}
